/**
 * handlers.visitReports.ts — offline replay for everything a rep submits FROM A
 * VISIT: the Besuchsbericht itself (legacy, v2 typed, v8 SPIN) and the
 * Bestellschein (scan-order) captured inside the visit questionnaire.
 *
 * Charter: ONE bounded context — visit submissions. It owns the
 * DELETE-ON-VERIFIED-ACK discipline: an outbox entry is pruned ONLY against a
 * durable server id, never against a bare 2xx. Registered by the `./handlers`
 * facade.
 */

import { registerQueueHandler } from './offlineQueue'
import {
  submitVisitReport,
  submitVisitReportV2,
  submitVisitReportV8,
} from '../api/missionVisits'
import { visitReportFinalCodeOf } from '../api/missionVisitReports'
import { FinalRefusalError } from './offlineQueueTypes'
import type {
  VisitReportPayload,
  VisitReportV2Payload,
  VisitReportV8Payload,
} from '../api/missionVisits.types'
import { confirmScanOrder } from '../api/scanOrders'
import type { ScanOrderConfirmPayload } from '../api/scanOrders'
import { QUEUE_TYPES } from './queueTypes'
import { registerQueuePredecessor, sourceEventIdOf } from './queueOrdering'

export type VisitReportQueuePayload = VisitReportPayload

// v2 typed Besuchsbericht queue payload. The stored body carries
// `source_event_id` (the idempotency key), so replaying it verbatim after a
// reconnect re-POSTs the SAME key — the SL collapses the duplicate rather than
// double-inserting. This is what makes the offline round-trip safe to retry.
export type VisitReportV2QueuePayload = VisitReportV2Payload

// v8 typed SPIN supplemental queue payload. Same idempotency discipline as v2:
// the stored body carries `source_event_id` (which resolves the visit engagement),
// so a replay after reconnect re-POSTs the SAME key and the RPC collapses the
// duplicate rather than double-attaching the SPIN collections.
export type VisitReportV8QueuePayload = VisitReportV8Payload

/**
 * Scan-order confirm — the reviewed Bestellschein push to Odoo. Queued when the
 * confirm POST fails transiently (the live 502 Odoo-cert reissue, network drop)
 * so the scan is never lost (BUG-2026-06-16-RVBDQ part D). Mirrors the
 * ScanOrderConfirmPayload the SL call replays.
 */
export type ScanOrderConfirmQueuePayload = ScanOrderConfirmPayload

/**
 * Failed-write capture reason for a queued v8 supplement whose v2 twin (same
 * source_event_id) is terminally failed in the outbox: the engagement was never minted,
 * so the v8 body was never sent. Joined to the v2 failure on the top-level
 * source_event_id (failedWriteCapture R4).
 */
export const V8_BEHIND_FAILED_V2_REASON = 'v2_twin_failed'

/**
 * Failed-write capture reason for a queued v8 supplement whose still-unlanded v2 twin the
 * rep DISCARDED: the engagement will never be minted from this device, so the v8 body is
 * pinned and captured instead of being replayed alone into a guaranteed refusal.
 */
export const V8_BEHIND_DISCARDED_V2_REASON = 'v2_twin_discarded'

export function registerVisitReportHandlers(): void {
  registerQueueHandler(QUEUE_TYPES.VISIT_REPORT_SUBMIT, async (payload) => {
    const p = payload as VisitReportQueuePayload
    await submitVisitReport(p)
  })

  registerQueueHandler(QUEUE_TYPES.VISIT_REPORT_V2_SUBMIT, async (payload) => {
    const p = payload as VisitReportV2QueuePayload
    // DELETE-ON-VERIFIED-ACK (FR-FE-IRONCLAD-TELEMETRY-OUTBOX): the response used to be
    // discarded, so a bare 2xx pruned the outbox entry with no proof the row landed.
    // Now the entry is removed ONLY when the server returns a durable visit_report_id.
    // A 2xx that carries no id is never a verified ack: the entry is kept, never deleted.
    // An acknowledgement naming ANOTHER write (review R2), or a 2xx outside the receipt contract (F1, a
    // receipt without engagement_id — Codex #2209 P1), is FINAL by its code: pinned on THIS replay and
    // captured, never retried (the server dedups on the key and answers the same). Nothing else changes.
    const res = await submitVisitReportV2(p).catch((err: unknown) => {
      const code = visitReportFinalCodeOf('v2', err)
      throw code === null ? err : new FinalRefusalError(code, err)
    })
    // DEAD LETTER = accepted-terminal: the server holds the report (projection_dead_letter)
    // and replays it once the lead resolves. Prune — retrying cannot attribute it sooner.
    // (The transport already recorded the dead letter's telemetry.)
    if (res.dead_letter !== null) return { verified: true, serverId: `dead_letter:${res.dead_letter.sourceEventId}` }
    if (res.visit_report_id) return { verified: true, serverId: res.visit_report_id }
    // A 2xx that confirmed no durable id is NOT proof the report landed. submitVisitReportV2 already
    // refuses it as VISIT_REPORT_V2_RESPONSE_SHAPE_INVALID (final above — Codex #2209 P1); asserted
    // again rather than trusted, so an entry is never pruned on an unconfirmed reply (D-33 R1).
    throw new Error(
      'visit-report-v2 replay returned success without a visit_report_id — refusing to '
      + 'confirm a delete on an unconfirmed write',
    )
  })

  registerQueueHandler(QUEUE_TYPES.VISIT_REPORT_V8_SUBMIT, async (payload) => {
    const p = payload as VisitReportV8QueuePayload
    // DELETE-ON-VERIFIED-ACK (same discipline as v2): the v8 RPC returns a REQUIRED
    // row-tally whose `engagement_id` is the durable proof the SPIN collections
    // attached. Remove the outbox entry ONLY when that id is present. submitVisitReportV8
    // already throws on a missing/malformed tally (D-33 R1), so a resolve here means a
    // real engagement_id — but assert it explicitly rather than trust the shape: a reply
    // without it is NOT a verified ack, so we throw to RETAIN + retry (the source_event_id
    // keeps the retry idempotent) rather than prune an unconfirmed write.
    // A refusal the door declares FINAL by its code (SL #2682 — not the caller's mission, stop not
    // on the mission, stop not found) is re-thrown as a FinalRefusalError: pinned on THIS replay and
    // captured, never retried — and so is an acknowledgement naming ANOTHER write (review R2). Any other
    // failure is re-thrown as it came and meets the generic status rule — under which every 4xx but
    // 401/408/429 (an unknown-code 403/404 included) is final too (retryClassification).
    const res = await submitVisitReportV8(p).catch((err: unknown) => {
      const code = visitReportFinalCodeOf('v8', err)
      throw code === null ? err : new FinalRefusalError(code, err)
    })
    // After a dead-lettered v2 the engagement never exists; the v8 call on the same key is
    // dead-lettered too — accepted-terminal, pruned, recorded.
    if (res.kind === 'dead_lettered') return { verified: true, serverId: `dead_letter:${res.sourceEventId}` }
    if (res.result.engagement_id) return { verified: true, serverId: res.result.engagement_id }
    throw new Error(
      'visit-report-v8 replay returned success without an engagement_id — refusing to '
      + 'confirm a delete on an unconfirmed write',
    )
  })

  // ONE CONTRACT, FIXED ORDER (RULING-EC-VISIT-REPORT-V8-OPEN-RULINGS-2026-10-04 R1): the
  // v8 body only supplements the engagement its v2 twin (same source_event_id) mints, so
  // a queued v8 entry never replays while that v2 entry is still unlanded in the outbox.
  // When the v2 twin is TERMINALLY failed the v8 entry is pinned failed and captured
  // under V8_BEHIND_FAILED_V2_REASON — never sent without v2, never left pending forever.
  // When the rep discards the unlanded v2 twin, the v8 entry takes the same terminal shape
  // under V8_BEHIND_DISCARDED_V2_REASON.
  registerQueuePredecessor(QUEUE_TYPES.VISIT_REPORT_V8_SUBMIT, {
    predecessorType: QUEUE_TYPES.VISIT_REPORT_V2_SUBMIT,
    keyOf: sourceEventIdOf,
    failedReasonCode: V8_BEHIND_FAILED_V2_REASON,
    discardedReasonCode: V8_BEHIND_DISCARDED_V2_REASON,
  })

  // Reviewed Bestellschein confirm — replay the Odoo push that 502'd at submit
  // (BUG-2026-06-16-RVBDQ part D). The body is the flat ScanOrderConfirmRequest
  // built at review time; replaying it re-pushes the same Freigabe.
  //
  // DELETE-ON-VERIFIED-ACK (this module's own charter, above): this handler
  // used to prune on a bare `await confirmScanOrder(p)` with no receipt check —
  // the one door in this file that didn't follow the charter it's registered
  // under. confirmScanOrder now returns the producer's own `status`, which is
  // a required field on every genuine 2xx; a resolve here means a real one.
  registerQueueHandler(QUEUE_TYPES.SCAN_ORDER_CONFIRM, async (payload) => {
    const p = payload as ScanOrderConfirmQueuePayload
    const res = await confirmScanOrder(p)
    return { verified: true, status: res.status }
  })
}
